/**
 * DESIGN.md §7.1 — Icon primitive.
 *
 * Stroke icons (no fill) with 1.5px stroke width, round caps + joins.
 * Color is `currentColor` so the icon inherits whatever `text-icon-*`
 * mapped token is applied to the parent. Size classes 20 / 24 / 32
 * match the §7.1 Figma masters, 16 is the §12.3 `16 px Icon` (294:2411)
 * added 2026-09-08 for the Dropdown carets / clear glyph, and a 40px
 * slot exists for the tablet/mobile hamburger.
 *
 * The icon inventory (`name` union) is the §7.1 documented set, the
 * hamburger glyph from §9.7, and the §12.3 additions (`arrow-up`,
 * `caret-up`, `caret-down`, `x`, `checkbox`) used by the spec 117
 * dashboard components. Adding a new icon: add a path in the switch,
 * extend the union. Strokes are 1.5px regardless of rendered size
 * (Figma's behavior: stroke is not scaled).
 */
import type { ReactElement, SVGProps } from 'react';
import { cn } from '../lib/cn.js';

export type IconName =
  | 'sign-in'
  | 'sign-out'
  | 'search'
  | 'circle-x'
  | 'arrow-right'
  | 'arrow-left'
  | 'check'
  | 'copy'
  | 'list'
  | 'chevron-down'
  | 'arrow-up'
  | 'caret-up'
  | 'caret-down'
  | 'x'
  | 'checkbox';

export type IconSize = 16 | 20 | 24 | 32 | 40;

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  name: IconName;
  size?: IconSize;
  /** Decorative by default. Provide `title` to make the icon semantic. */
  title?: string;
}

const SIZE_CLASS: Record<IconSize, string> = {
  16: 'size-4',
  20: 'size-5',
  24: 'size-6',
  32: 'size-8',
  40: 'size-10',
};

export function Icon({ name, size = 20, title, className, ...props }: IconProps) {
  const decorative = title === undefined;
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={cn(SIZE_CLASS[size], className)}
      role={decorative ? undefined : 'img'}
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : title}
      {...props}
    >
      {title ? <title>{title}</title> : null}
      <IconPath name={name} />
    </svg>
  );
}

function IconPath({ name }: { name: IconName }): ReactElement {
  switch (name) {
    case 'sign-in':
      return (
        <>
          <path d="M15 12H4" />
          <path d="m11 8 4 4-4 4" />
          <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
        </>
      );
    case 'sign-out':
      // Mirror of sign-in: door on the left, arrow pointing OUT to the right.
      return (
        <>
          <path d="M9 12h11" />
          <path d="m16 8 4 4-4 4" />
          <path d="M9 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h4" />
        </>
      );
    case 'search':
      return (
        <>
          <circle cx="11" cy="11" r="7" />
          <path d="m20 20-3.5-3.5" />
        </>
      );
    case 'circle-x':
      return (
        <>
          <circle cx="12" cy="12" r="9" />
          <path d="m9 9 6 6" />
          <path d="m15 9-6 6" />
        </>
      );
    case 'arrow-right':
      return (
        <>
          <path d="M5 12h14" />
          <path d="m13 6 6 6-6 6" />
        </>
      );
    case 'arrow-left':
      return (
        <>
          <path d="M19 12H5" />
          <path d="m11 6-6 6 6 6" />
        </>
      );
    case 'check':
      return <path d="M5 12.5 10 17.5 19 7" />;
    case 'copy':
      // Two offset rounded squares — the universal "copy to clipboard" glyph.
      return (
        <>
          <rect x="9" y="9" width="11" height="11" rx="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </>
      );
    case 'list':
      return (
        <>
          <path d="M4 6h16" />
          <path d="M4 12h16" />
          <path d="M4 18h16" />
        </>
      );
    case 'chevron-down':
      return <path d="m6 9 6 6 6-6" />;
    case 'arrow-up':
      // §12.4 Md Icon Btn glyph — the mirror of arrow-right, pointing up.
      return (
        <>
          <path d="M12 19V5" />
          <path d="m6 11 6-6 6 6" />
        </>
      );
    case 'caret-up':
      // §12.4 Dropdown, open state (16px).
      return <path d="m7 14 5-5 5 5" />;
    case 'caret-down':
      // §12.4 Dropdown, default state (16px).
      return <path d="m7 10 5 5 5-5" />;
    case 'x':
      // §12.3 — the bare close glyph (no circle), 16px.
      return (
        <>
          <path d="m6 6 12 12" />
          <path d="m18 6-12 12" />
        </>
      );
    case 'checkbox':
      // §12.4 Checkbox Field — the 20px check drawn inside the box; the
      // box itself is the component's border, so the glyph is the tick.
      return <path d="m6 12.5 4 4 8-9" />;
  }
}
